import { useEffect, useMemo, useState } from 'react';
import { useProducts } from '@/hooks/useProducts';
import type { Drone } from '@/types/drones';

interface UseProductDetailResult {
  product: Drone | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and return product details by ID and type
 */
export function useProductDetail(
  id: string,
  type: 'new' | 'used' | 'buffalo' | 'power-citros' | 'parts'
): UseProductDetailResult {
  const [error, setError] = useState<string | null>(null);

  // Map type to category folder
  const categoryMap = {
    new: 'new-drones',
    used: 'used-drones',
    buffalo: 'buffalo',
    'power-citros': 'power-citros',
    parts: 'parts',
  };

  const { products, loading } = useProducts(categoryMap[type]);

  const product = useMemo(() => {
    try {
      return products.find((drone) => drone.id === id) || null;
    } catch {
      setError('Failed to load product');
      return null;
    }
  }, [id, products]);

  useEffect(() => {
    if (!loading && !product && id && type) {
      setError('Product not found');
    } else if (product) {
      setError(null);
    }
  }, [product, id, type, loading]);

  return {
    product,
    isLoading: loading,
    error,
  };
}
